import { t, type IntlService } from 'ember-intl'
import { LinkTo } from '@ember/routing'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { on } from '@ember/modifier'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import type Store from '@ember-data/store'
import type FileDownload from 'teacher-ui-client/services/file-download'
import { service } from '@ember/service'
import { action } from '@ember/object'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type RouterService from '@ember/routing/router-service'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { task } from 'ember-concurrency'
import type Teacher from 'teacher-ui-client/models/teacher'

export class ManageRolloverStepsTeachersPage extends Component<unknown> {
  @service store!: Store

  @service fileDownload!: FileDownload

  @service intl!: IntlService

  @service subscriptionType!: SubscriptionTypeService

  @service router!: RouterService

  /**
   * We only (currently) support Reading and Maths rollovers
   */
  get currentSubscriptionType(): SubscriptionType.Reading | SubscriptionType.Maths {
    if (
      this.subscriptionType.current === SubscriptionType.Reading ||
      this.subscriptionType.current === SubscriptionType.Maths
    ) {
      return this.subscriptionType.current
    } else {
      return SubscriptionType.Reading
    }
  }

  get iconColorClass() {
    const baseClass = 'h-6 w-6'
    return {
      [SubscriptionType.Reading]: `text-oceany-blue-350 ${baseClass}`,
      [SubscriptionType.Maths]: `text-ms-green-300 ${baseClass}`,
    }[this.currentSubscriptionType]
  }

  @action
  transitionToManageTeachers() {
    void this.router.transitionTo('manage.teachers')
  }

  downloadTeacherCsvTask = task({ drop: true }, async () => {
    const teachers = await this.store.findAll<Teacher>('teacher')

    const { intl } = this
    const keyPrefix = 'manage.rollover.steps.teachers.export'

    const fields = [`${keyPrefix}.fields.firstName`, `${keyPrefix}.fields.lastName`, `${keyPrefix}.fields.email`].map(
      (key) => intl.t(key),
    )
    const data = teachers.map((teacher) => [teacher.firstName, teacher.lastName, teacher.email])
    const parseData = [fields, ...data]

    const parseConfig = { filename: intl.t(`${keyPrefix}.filename`) }

    this.fileDownload.downloadAsCsv(parseData, parseConfig)
  }); /* prettier-ignore */ /* prettier-ignore-end */

  <template>
    <div class="mx-auto max-w-3xl px-2">
      <div class="mb-12">
        <h3 class="text-dusty-black-250 mt-8 mb-6">
          {{t "manage.rollover.steps.teachers.stepContentHeader"}}
        </h3>
        <div class="space-y-6 text-xl">
          <p>{{t "manage.rollover.steps.teachers.allTeachersNeedAccount"}}</p>
          <p>
            {{t "manage.rollover.steps.teachers.useManageTeachers"}}
            <LinkTo @route="manage.teachers">{{t "manage.rollover.steps.teachers.manageTeachers"}}</LinkTo>.
          </p>
          <p>
            {{t
              "manage.rollover.steps.teachers.canSkipStep"
              studentsStepHeader=(t "manage.rollover.steps.students.stepContentHeader")
              htmlSafe=true
            }}
          </p>
        </div>
      </div>

      <div class="mb-12">
        <p class="mb-3 text-xl">
          {{#if this.downloadTeacherCsvTask.isIdle}}
            <PhosphorIcon @icon="phosphor-download-fill" class={{this.iconColorClass}} />
          {{else}}
            <PhosphorIcon @icon="phosphor-circle-notch-bold" class="h-8 w-8 animate-spin" />
          {{/if}}
          <span
            data-test-csv-download-button
            class="font-bold text-black underline hover:text-black focus:text-black"
            role="button"
            {{on "click" this.downloadTeacherCsvTask.perform}}
          >{{t "manage.rollover.steps.teachers.export.downloadLink"}}</span>
          {{t
            "manage.rollover.steps.teachers.export.downloadDescription"
            studentsStepHeader=(t "manage.rollover.steps.students.stepContentHeader")
            htmlSafe=true
          }}
        </p>
      </div>

      <div class="text-right">
        <UiButton @appearance={{ButtonAppearance.regular}} {{on "click" this.transitionToManageTeachers}}>
          {{t "manage.rollover.steps.teachers.manageTeachersButton"}}
        </UiButton>
      </div>
    </div>
  </template>
}

export default RouteTemplate(ManageRolloverStepsTeachersPage)
